import type { ReactNode } from 'react';
import type { LevelView, TrailCrumb } from '@/lib/filing-trail';

interface FilingLevelProps {
  /** All categories › Phase › … › the level's parent (lib/filing-trail). */
  crumbs: TrailCrumb[];
  /** The options' level in the category tree (1 = top-level categories). */
  level: number;
  /** Step back up to a crumb's level to choose again from there. */
  onStep: (view: LevelView) => void;
  /** Set while showing a level the person stepped back to: returns to the
   * deepest category the document is filed in. */
  onReturn?: { label: string; go: () => void };
  /** The level's options (and its "+ New category here" row). */
  children: ReactNode;
}

/**
 * One filing level with WHERE it is made obvious (owner ask 2026-10-15): a
 * breadcrumb from the top — All categories › Phase › Category › … › choose
 * next — whose crumbs step back up to that level, then the level's options
 * nested under the last crumb with a "Level N" label. Used by the review queue
 * and the "Review classification" dialog alike.
 */
export function FilingLevel({ crumbs, level, onStep, onReturn, children }: FilingLevelProps) {
  const last = crumbs.length - 1;
  return (
    <div>
      <nav aria-label="Where this document is being filed">
        <ol className="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm">
          {crumbs.map((crumb, i) => (
            <li key={crumb.key} className="flex min-w-0 items-center gap-1">
              {i < last ? (
                <button
                  type="button"
                  onClick={() => onStep(crumb.view)}
                  title={`Go back up to ${crumb.label} and choose again from there`}
                  className="max-w-full truncate rounded px-1 py-0.5 font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {crumb.label}
                </button>
              ) : (
                <span aria-current="location" className="min-w-0 break-words px-1 font-semibold">
                  {crumb.label}
                </span>
              )}
              <span aria-hidden="true" className="text-ink-3">
                ›
              </span>
            </li>
          ))}
          <li>
            <span className="inline-flex items-center rounded border border-dashed border-primary px-2 py-0.5 text-xs font-semibold text-primary">
              choose next
            </span>
          </li>
        </ol>
      </nav>
      <div className="ml-2 mt-2 border-l-2 border-primary/30 pl-3">
        <p className="flex flex-wrap items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <span>Level {level}</span>
          {onReturn ? (
            <button
              type="button"
              onClick={onReturn.go}
              className="normal-case tracking-normal text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Back to {onReturn.label}
            </button>
          ) : null}
        </p>
        {children}
      </div>
    </div>
  );
}
